import React, { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const Events = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    location: "",
    event_type: "In Person",
    cost: "",
    image_url: "",
    event_url: ""
  });
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get("/events");
      setEvents(response.data);
    } catch (error) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/events", formData);
      toast.success("Event created successfully!");
      setCreateOpen(false);
      setFormData({
        title: "",
        description: "",
        date: "",
        location: "",
        event_type: "In Person",
        cost: "",
        image_url: "",
        event_url: ""
      });
      fetchEvents();
    } catch (error) {
      toast.error("Failed to create event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await axiosInstance.delete(`/events/${eventId}`);
      toast.success("Event deleted");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div data-testid="events-page">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>Events</h1>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-event-button" className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6">
              Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Event Title</Label>
                <Input
                  id="title"
                  data-testid="event-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  data-testid="event-date-input"
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  data-testid="event-location-input"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="event-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={4}
                  required
                />
              </div>
              <div>
                <Label>Event Image (Optional)</Label>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="image_file" className="text-sm text-gray-600">Upload from device</Label>
                    <Input
                      id="image_file"
                      data-testid="event-image-file-input"
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error("Image must be less than 2MB");
                            e.target.value = "";
                            return;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFormData({...formData, image_url: reader.result});
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Max 2MB</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="text-xs text-gray-500">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  <div>
                    <Label htmlFor="image_url" className="text-sm text-gray-600">Paste image URL</Label>
                    <Input
                      id="image_url"
                      data-testid="event-image-url-input"
                      type="url"
                      value={formData.image_url?.startsWith('data:') ? '' : formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                  {formData.image_url && (
                    <div className="mt-2">
                      <img src={formData.image_url} alt="Preview" className="max-h-32 rounded border" />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="event_type">Event Type</Label>
                <Select value={formData.event_type} onValueChange={(value) => setFormData({...formData, event_type: value})}>
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In Person">In Person</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cost">Cost (Optional)</Label>
                <Input
                  id="cost"
                  data-testid="event-cost-input"
                  value={formData.cost}
                  onChange={(e) => setFormData({...formData, cost: e.target.value})}
                  placeholder="e.g., Free, $10, $25/person"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for free events</p>
              </div>
              <div>
                <Label htmlFor="event_url">Event Website/Registration URL (Optional)</Label>
                <Input
                  id="event_url"
                  data-testid="event-url-input"
                  type="url"
                  value={formData.event_url}
                  onChange={(e) => setFormData({...formData, event_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              <Button data-testid="submit-event-button" type="submit" className="w-full bg-teal-500 hover:bg-teal-600">Create Event</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <Card 
            key={event.id} 
            data-testid={`event-card-${event.id}`} 
            className="bg-white/90 backdrop-blur-sm border-teal-100 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => setSelectedEvent(event)}
          >
            {event.image_url && (
              <div className="h-48 overflow-hidden rounded-t-lg">
                <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
              </div>
            )}
            <CardHeader>
              <CardTitle className="text-xl" style={{ fontFamily: 'Merriweather, serif' }}>{event.title}</CardTitle>
              <CardDescription>
                <div className="flex flex-col gap-1 text-base">
                  <span>📅 {new Date(event.date).toLocaleString()}</span>
                  <span>📍 {event.location}</span>
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                  {event.event_type || "In Person"}
                </span>
                {event.cost && (
                  <span className="text-sm text-gray-600 font-medium">{event.cost}</span>
                )}
                {!event.cost && (
                  <span className="text-sm text-green-600 font-medium">Free</span>
                )}
              </div>
              <p className="text-gray-600 text-sm line-clamp-3 mb-4">{event.description}</p>
              <div className="flex gap-2">
                <Button 
                  data-testid={`view-event-${event.id}`}
                  variant="default"
                  className="flex-1 bg-teal-500 hover:bg-teal-600 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedEvent(event);
                  }}
                >
                  View Details
                </Button>
                {(user.id === event.user_id || user.is_admin) && (
                  <Button 
                    data-testid={`delete-event-${event.id}`}
                    variant="destructive" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(event.id);
                    }}
                    className="rounded-full"
                  >
                    {user.is_admin && user.id !== event.user_id ? "Remove" : "Delete"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {events.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No events yet. Create the first one!</p>
        </div>
      )}

      {/* Event Detail Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                  {selectedEvent.title}
                </DialogTitle>
              </DialogHeader>
              
              {selectedEvent.image_url && (
                <div className="h-64 overflow-hidden rounded-lg mb-4">
                  <img src={selectedEvent.image_url} alt={selectedEvent.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                    {selectedEvent.event_type || "In Person"}
                  </span>
                  {selectedEvent.cost ? (
                    <span className="text-base text-gray-700 font-medium">Cost: {selectedEvent.cost}</span>
                  ) : (
                    <span className="text-base text-green-600 font-medium">Free</span>
                  )}
                </div>
                <div className="flex flex-col gap-2 text-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">📅 Date:</span>
                    <span>{new Date(selectedEvent.date).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">📍 Location:</span>
                    <span>{selectedEvent.location}</span>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>Description</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>

                <div className="border-t pt-4 space-y-3">
                  {/* Calendar Links */}
                  <div>
                    <h3 className="text-lg font-bold mb-2">Add to Calendar</h3>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => {
                          const eventDate = new Date(selectedEvent.date);
                          const endDate = new Date(eventDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours later
                          const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(selectedEvent.title)}&dates=${eventDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z&details=${encodeURIComponent(selectedEvent.description)}&location=${encodeURIComponent(selectedEvent.location)}`;
                          window.open(googleCalUrl, '_blank');
                        }}
                        data-testid="google-calendar-button"
                      >
                        Google Calendar
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => {
                          const eventDate = new Date(selectedEvent.date);
                          const endDate = new Date(eventDate.getTime() + 2 * 60 * 60 * 1000);
                          const icsContent = [
                            'BEGIN:VCALENDAR',
                            'VERSION:2.0',
                            'BEGIN:VEVENT',
                            `DTSTART:${eventDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
                            `DTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
                            `SUMMARY:${selectedEvent.title}`,
                            `DESCRIPTION:${selectedEvent.description.replace(/\n/g, '\\n')}`,
                            `LOCATION:${selectedEvent.location}`,
                            'END:VEVENT',
                            'END:VCALENDAR'
                          ].join('\n');
                          const blob = new Blob([icsContent], { type: 'text/calendar' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${selectedEvent.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
                          link.click();
                          URL.revokeObjectURL(url);
                        }}
                        data-testid="download-ics-button"
                      >
                        Download .ics
                      </Button>
                    </div>
                  </div>

                  {/* Event URL */}
                  {selectedEvent.event_url && (
                    <div>
                      <h3 className="text-lg font-bold mb-2">Event Website</h3>
                      <a 
                        href={selectedEvent.event_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-700 font-medium break-all"
                        data-testid="event-website-link"
                      >
                        {selectedEvent.event_url}
                      </a>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  {selectedEvent.event_url && (
                    <Button 
                      className="flex-1 bg-teal-500 hover:bg-teal-600 rounded-full"
                      onClick={() => window.open(selectedEvent.event_url, '_blank')}
                      data-testid="visit-website-button"
                    >
                      Visit Website
                    </Button>
                  )}
                  {user.id === selectedEvent.user_id && (
                    <Button 
                      variant="destructive"
                      onClick={() => {
                        handleDelete(selectedEvent.id);
                        setSelectedEvent(null);
                      }}
                      className="rounded-full"
                      data-testid="delete-event-modal"
                    >
                      Delete Event
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;